import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  MODAIS,
  type Aviso,
  type FaturaEmbarqueEnvio,
  type ItemEmbarqueEnvio,
  type SituacaoEmbarque,
} from '@pcp/contracts';
import {
  useAtualizarEmbarque,
  useCriarEmbarque,
  useEmbarque,
  useOrdens,
} from '../../api/embarques';
import { ApiRequestError } from '../../api/client';
import { useMe } from '../../api/queries';
import { ErrorState, LoadingBlock } from '../../components/states';
import { t } from '../../i18n/pt-BR';
import { SituacaoChip } from './ImportacaoPage';
import { PERFIS_ESCRITA, PERFIS_FINANCEIRO } from './shared';

const f = t.shipments.form;
const SITUACOES: SituacaoEmbarque[] = [1, 2, 3, 4, 5, 6, 7];

type Cab = Record<string, string>;
const CAMPOS_TEXTO = [
  ['exportador', f.exporter],
  ['incoterm', f.incoterm],
  ['origem', f.origin],
  ['despachante', f.broker],
  ['terminal', f.terminal],
  ['bl-awb', f.blAwb],
  ['nr-nf', f.nf],
  ['drawback', f.drawback],
] as const;
const CAMPOS_DATA = [
  ['dt-bl-awb', f.dtBl],
  ['dt-etd-prev', f.etdPrev],
  ['dt-etd-real', f.etdReal],
  ['dt-eta-prev', f.etaPrev],
  ['dt-eta-real', f.etaReal],
  ['dt-desemb-prev', f.desembPrev],
  ['dt-desemb-real', f.desembReal],
  ['dt-entrega-prev', f.entregaPrev],
  ['dt-entrega-real', f.entregaReal],
  ['dt-nf', f.dtNf],
  ['dt-armazenagem-venc', f.storageDue],
] as const;

type ItemForm = {
  seq?: number;
  version?: string;
  'it-codigo': string;
  'numero-ordem': string;
  quantidade: string;
  un: string;
  preco: string;
  'sem-ordem': boolean;
};
type FaturaForm = Record<
  'nr-invoice' | 'dt-invoice' | 'valor-invoice' | 'moeda' | 'valor-brl' | 'dt-venc-invoice' | 'cond-pagto' | 'custo-desemb-brl' | 'version',
  string
>;

const num = (v: string) => (v.trim() === '' ? undefined : Number(v.replace(',', '.')));
const txt = (v: string) => (v.trim() === '' ? undefined : v.trim());

/** Formulário do embarque: cabeçalho, itens (ordem escolhida da lista de ordens abertas) e faturas. */
export function EmbarquePage() {
  const { nrImport } = useParams();
  const novo = !nrImport || nrImport === 'novo';
  const nr = novo ? '' : decodeURIComponent(nrImport!);
  const navigate = useNavigate();
  const me = useMe();
  const perfil = me.data?.perfil;
  const podeEscrever = !!perfil && PERFIS_ESCRITA.includes(perfil);
  const veFinanceiro = !!perfil && PERFIS_FINANCEIRO.includes(perfil);

  const atual = useEmbarque(novo ? undefined : nr);
  const criar = useCriarEmbarque();
  const atualizar = useAtualizarEmbarque(nr);

  const [numero, setNumero] = useState('');
  const [situacao, setSituacao] = useState<SituacaoEmbarque>(1);
  const [modal, setModal] = useState('');
  const [cab, setCab] = useState<Cab>({});
  const [obs, setObs] = useState('');
  const [itens, setItens] = useState<ItemForm[]>([
    { 'it-codigo': '', 'numero-ordem': '', quantidade: '', un: 'KG', preco: '', 'sem-ordem': false },
  ]);
  const [faturas, setFaturas] = useState<FaturaForm[]>([]);
  const [version, setVersion] = useState('');
  const [erros, setErros] = useState<{ campo: string; mensagem: string }[]>([]);
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  const [exigeConfirmacao, setExigeConfirmacao] = useState(false);
  const [conflito, setConflito] = useState(false);
  const [mensagem, setMensagem] = useState<string | null>(null);

  useEffect(() => {
    const e = atual.data;
    if (!e) return;
    setNumero(e['nr-import']);
    setSituacao(e.situacao);
    setModal(e.modal ?? '');
    setObs(e.obs ?? '');
    setVersion(e.version);
    const c: Cab = {};
    for (const [k] of [...CAMPOS_TEXTO, ...CAMPOS_DATA]) {
      const v = (e as Record<string, unknown>)[k];
      if (typeof v === 'string') c[k] = v;
    }
    setCab(c);
    setItens(
      e.itens.map((i) => ({
        seq: i.seq,
        version: i.version,
        'it-codigo': i['it-codigo'],
        'numero-ordem': i['numero-ordem'] ? String(i['numero-ordem']) : '',
        quantidade: String(i.quantidade),
        un: i.un,
        preco: i.preco === undefined ? '' : String(i.preco),
        'sem-ordem': i['sem-ordem'],
      })),
    );
    setFaturas(
      (e.faturas ?? []).map((x) => ({
        'nr-invoice': x['nr-invoice'],
        'dt-invoice': x['dt-invoice'] ?? '',
        'valor-invoice': x['valor-invoice'] === undefined ? '' : String(x['valor-invoice']),
        moeda: x.moeda ?? '',
        'valor-brl': x['valor-brl'] === undefined ? '' : String(x['valor-brl']),
        'dt-venc-invoice': x['dt-venc-invoice'] ?? '',
        'cond-pagto': x['cond-pagto'] ?? '',
        'custo-desemb-brl': x['custo-desemb-brl'] === undefined ? '' : String(x['custo-desemb-brl']),
        version: x.version,
      })),
    );
    setConflito(false);
  }, [atual.data]);

  const setCampo = (k: string, v: string) => setCab((c) => ({ ...c, [k]: v }));
  const setItem = (idx: number, patch: Partial<ItemForm>) =>
    setItens((l) => l.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  const setFatura = (idx: number, patch: Partial<FaturaForm>) =>
    setFaturas((l) => l.map((x, i) => (i === idx ? { ...x, ...patch } : x)));

  const cabecalho = () => {
    const out: Record<string, unknown> = { situacao, obs: txt(obs), modal: modal || undefined };
    for (const [k] of [...CAMPOS_TEXTO, ...CAMPOS_DATA]) out[k] = txt(cab[k] ?? '');
    return out;
  };
  const itensEnvio = (): ItemEmbarqueEnvio[] =>
    itens.map((i) => ({
      ...(i.seq ? { seq: i.seq } : {}),
      ...(i.version ? { version: i.version } : {}),
      'it-codigo': i['it-codigo'].trim().toUpperCase(),
      'numero-ordem': i['numero-ordem'] ? Number(i['numero-ordem']) : undefined,
      quantidade: num(i.quantidade) ?? 0,
      un: i.un.trim().toUpperCase(),
      preco: num(i.preco),
      'sem-ordem': i['sem-ordem'],
    }));
  const faturasEnvio = (): FaturaEmbarqueEnvio[] =>
    faturas
      .filter((x) => x['nr-invoice'].trim())
      .map((x) => ({
        'nr-invoice': x['nr-invoice'].trim(),
        'dt-invoice': txt(x['dt-invoice']),
        'valor-invoice': num(x['valor-invoice']),
        moeda: txt(x.moeda),
        'valor-brl': num(x['valor-brl']),
        'dt-venc-invoice': txt(x['dt-venc-invoice']),
        'cond-pagto': txt(x['cond-pagto']),
        'custo-desemb-brl': num(x['custo-desemb-brl']),
        ...(x.version ? { version: x.version } : {}),
      }));

  const gravar = async (confirmar: boolean) => {
    setErros([]);
    setAvisos([]);
    setExigeConfirmacao(false);
    setConflito(false);
    setMensagem(null);
    try {
      if (novo) {
        const r = await criar.mutateAsync({
          embarque: { 'nr-import': numero.trim(), ...cabecalho() } as never,
          itens: itensEnvio(),
          faturas: veFinanceiro ? faturasEnvio() : [],
          confirmar,
        });
        navigate(`/importacao/${encodeURIComponent(r.embarque['nr-import'])}`, { replace: true });
      } else {
        const r = await atualizar.mutateAsync({
          version,
          embarque: cabecalho() as never,
          itens: itensEnvio(),
          ...(veFinanceiro ? { faturas: faturasEnvio() } : {}),
          confirmar,
        });
        setAvisos(r.avisos);
        setMensagem(f.saved);
        void atual.refetch();
      }
    } catch (e) {
      if (e instanceof ApiRequestError) {
        if (e.isConflict) return setConflito(true);
        const d = e.details as
          | { erros?: { campo: string; mensagem: string }[]; avisos?: Aviso[]; requerConfirmacao?: boolean }
          | undefined;
        setErros(d?.erros ?? (d?.requerConfirmacao ? [] : [{ campo: '', mensagem: e.message }]));
        setAvisos(d?.avisos ?? []);
        setExigeConfirmacao(!!d?.requerConfirmacao);
      } else {
        setErros([{ campo: '', mensagem: t.states.datasulDown }]);
      }
    }
  };

  if (!novo && atual.isLoading) return <LoadingBlock rows={6} />;
  if (!novo && atual.isError) return <ErrorState error={atual.error} onRetry={() => void atual.refetch()} />;

  const salvando = criar.isPending || atualizar.isPending;
  const somenteLeitura = !podeEscrever;

  return (
    <section className="panel" aria-labelledby="embf-title">
      <p>
        <Link to="/importacao">{f.back}</Link>
      </p>
      <h2 id="embf-title">{novo ? f.titleNew : f.title(nr)}</h2>
      {!novo ? <SituacaoChip situacao={situacao} /> : null}
      {somenteLeitura ? <p className="hint">{t.shipments.readOnly}</p> : null}

      {conflito ? (
        <div className="banner warn" role="alert">
          {f.conflict}{' '}
          <button type="button" className="btn ghost" onClick={() => void atual.refetch()}>
            {f.reload}
          </button>
        </div>
      ) : null}
      {erros.length ? (
        <div className="form-error" role="alert">
          <strong>{f.errors}</strong>
          <ul>
            {erros.map((x, i) => (
              <li key={i}>
                {x.campo ? `${x.campo}: ` : ''}
                {x.mensagem}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {avisos.length ? (
        <div className="banner warn" role="status">
          <strong>{f.warnings}</strong>
          <ul>
            {avisos.map((x, i) => (
              <li key={i}>{x.mensagem}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {mensagem ? (
        <div className="banner ok" role="status">
          {mensagem}
        </div>
      ) : null}

      <fieldset disabled={somenteLeitura} style={{ border: 0, padding: 0 }}>
        <h3>{f.header}</h3>
        <div className="form-grid">
          <div className="field">
            <label htmlFor="e-nr">{f.nr}</label>
            <input id="e-nr" value={numero} disabled={!novo} onChange={(e) => setNumero(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="e-sit">{f.status}</label>
            <select
              id="e-sit"
              className="select"
              value={situacao}
              onChange={(e) => setSituacao(Number(e.target.value) as SituacaoEmbarque)}
            >
              {SITUACOES.map((n) => (
                <option key={n} value={n}>
                  {t.shipments.status[n]}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="e-modal">{f.modal}</label>
            <select id="e-modal" className="select" value={modal} onChange={(e) => setModal(e.target.value)}>
              <option value="">—</option>
              {MODAIS.map((m) => (
                <option key={m} value={m}>
                  {t.shipments.modal[m]}
                </option>
              ))}
            </select>
          </div>
          {CAMPOS_TEXTO.map(([k, label]) => (
            <div className="field" key={k}>
              <label htmlFor={`e-${k}`}>{label}</label>
              <input id={`e-${k}`} value={cab[k] ?? ''} onChange={(e) => setCampo(k, e.target.value)} />
            </div>
          ))}
          {CAMPOS_DATA.map(([k, label]) => (
            <div className="field" key={k}>
              <label htmlFor={`e-${k}`}>{label}</label>
              <input
                id={`e-${k}`}
                type="date"
                value={cab[k] ?? ''}
                onChange={(e) => setCampo(k, e.target.value)}
              />
            </div>
          ))}
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label htmlFor="e-obs">{f.obs}</label>
            <input id="e-obs" value={obs} maxLength={1000} onChange={(e) => setObs(e.target.value)} />
          </div>
        </div>

        <h3>{f.items}</h3>
        <div className="scroll">
          <table className="cad-table">
            <thead>
              <tr>
                <th>{f.item}</th>
                <th>{f.order}</th>
                <th className="n">{f.qty}</th>
                <th>{f.unit}</th>
                <th className="n">{f.price}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {itens.map((it, idx) => (
                <LinhaItem
                  key={idx}
                  item={it}
                  bloqueado={somenteLeitura}
                  onChange={(p) => setItem(idx, p)}
                  onRemove={itens.length > 1 ? () => setItens((l) => l.filter((_, i) => i !== idx)) : undefined}
                />
              ))}
            </tbody>
          </table>
        </div>
        {!somenteLeitura ? (
          <button
            type="button"
            className="btn ghost"
            onClick={() =>
              setItens((l) => [
                ...l,
                { 'it-codigo': '', 'numero-ordem': '', quantidade: '', un: 'KG', preco: '', 'sem-ordem': false },
              ])
            }
          >
            {f.addItem}
          </button>
        ) : null}

        {veFinanceiro ? (
          <>
            <h3>{f.invoices}</h3>
            <div className="scroll">
              <table className="cad-table">
                <thead>
                  <tr>
                    <th>{f.invoice}</th>
                    <th>{f.invoiceDate}</th>
                    <th className="n">{f.amount}</th>
                    <th>{f.currency}</th>
                    <th className="n">{f.amountBrl}</th>
                    <th>{f.due}</th>
                    <th>{f.payment}</th>
                    <th className="n">{f.clearanceCost}</th>
                  </tr>
                </thead>
                <tbody>
                  {faturas.map((x, idx) => (
                    <tr key={idx}>
                      <td>
                        <input
                          className="cell-input"
                          aria-label={f.invoice}
                          value={x['nr-invoice']}
                          disabled={!!x.version}
                          onChange={(e) => setFatura(idx, { 'nr-invoice': e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="cell-input"
                          type="date"
                          aria-label={f.invoiceDate}
                          value={x['dt-invoice']}
                          onChange={(e) => setFatura(idx, { 'dt-invoice': e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="cell-input n"
                          aria-label={f.amount}
                          value={x['valor-invoice']}
                          onChange={(e) => setFatura(idx, { 'valor-invoice': e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="cell-input"
                          aria-label={f.currency}
                          value={x.moeda}
                          onChange={(e) => setFatura(idx, { moeda: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="cell-input n"
                          aria-label={f.amountBrl}
                          value={x['valor-brl']}
                          onChange={(e) => setFatura(idx, { 'valor-brl': e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="cell-input"
                          type="date"
                          aria-label={f.due}
                          value={x['dt-venc-invoice']}
                          onChange={(e) => setFatura(idx, { 'dt-venc-invoice': e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="cell-input"
                          aria-label={f.payment}
                          value={x['cond-pagto']}
                          onChange={(e) => setFatura(idx, { 'cond-pagto': e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="cell-input n"
                          aria-label={f.clearanceCost}
                          value={x['custo-desemb-brl']}
                          onChange={(e) => setFatura(idx, { 'custo-desemb-brl': e.target.value })}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {podeEscrever ? (
              <button
                type="button"
                className="btn ghost"
                onClick={() =>
                  setFaturas((l) => [
                    ...l,
                    {
                      'nr-invoice': '',
                      'dt-invoice': '',
                      'valor-invoice': '',
                      moeda: '',
                      'valor-brl': '',
                      'dt-venc-invoice': '',
                      'cond-pagto': '',
                      'custo-desemb-brl': '',
                      version: '',
                    },
                  ])
                }
              >
                {f.addInvoice}
              </button>
            ) : null}
          </>
        ) : null}
      </fieldset>

      {podeEscrever ? (
        <div className="row" style={{ marginTop: 16 }}>
          <button type="button" className="btn" disabled={salvando} onClick={() => void gravar(false)}>
            {salvando ? f.saving : f.save}
          </button>
          {exigeConfirmacao ? (
            <button type="button" className="btn ghost" disabled={salvando} onClick={() => void gravar(true)}>
              {f.confirm}
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function LinhaItem({
  item,
  bloqueado,
  onChange,
  onRemove,
}: {
  item: ItemForm;
  bloqueado: boolean;
  onChange: (patch: Partial<ItemForm>) => void;
  onRemove?: () => void;
}) {
  const ordens = useOrdens(item['it-codigo'], !bloqueado);
  return (
    <tr>
      <td>
        <input
          className="cell-input"
          aria-label={f.item}
          value={item['it-codigo']}
          disabled={bloqueado || !!item.version}
          onChange={(e) => onChange({ 'it-codigo': e.target.value, 'numero-ordem': '' })}
        />
      </td>
      <td>
        {item['sem-ordem'] && !item['numero-ordem'] ? (
          <em>{f.noOrderMigrated}</em>
        ) : (
          <select
            className="select"
            aria-label={f.order}
            value={item['numero-ordem']}
            disabled={bloqueado}
            onChange={(e) => onChange({ 'numero-ordem': e.target.value })}
          >
            <option value="">
              {ordens.data && !ordens.data.length && item['it-codigo'] ? f.noOrders : f.chooseOrder}
            </option>
            {item['numero-ordem'] && !ordens.data?.some((o) => String(o['numero-ordem']) === item['numero-ordem']) ? (
              <option value={item['numero-ordem']}>{item['numero-ordem']}</option>
            ) : null}
            {(ordens.data ?? []).map((o) => (
              <option key={o['numero-ordem']} value={o['numero-ordem']}>
                {o['numero-ordem']} · pedido {o['num-pedido']} · saldo {o['quant-saldo']} {o.un}
              </option>
            ))}
          </select>
        )}
      </td>
      <td>
        <input
          className="cell-input n"
          aria-label={f.qty}
          value={item.quantidade}
          disabled={bloqueado}
          onChange={(e) => onChange({ quantidade: e.target.value })}
        />
      </td>
      <td>
        <input
          className="cell-input"
          aria-label={f.unit}
          value={item.un}
          disabled={bloqueado}
          onChange={(e) => onChange({ un: e.target.value })}
        />
      </td>
      <td>
        <input
          className="cell-input n"
          aria-label={f.price}
          value={item.preco}
          disabled={bloqueado}
          onChange={(e) => onChange({ preco: e.target.value })}
        />
      </td>
      <td>
        {onRemove && !bloqueado ? (
          <button type="button" className="btn ghost" onClick={onRemove}>
            {f.removeItem}
          </button>
        ) : null}
      </td>
    </tr>
  );
}
